import type { PaymentFileRowResult, PaymentStatusId } from '@steerfi/shared';
import {
  getPaymentFileRowFailureDisplayMessage,
  PAYMENT_STATUS,
  ROW_PROCESSING_STATUS,
} from '@steerfi/shared';
import { FileText } from 'lucide-react';
import { useState } from 'react';
import type { PaymentWithRelations } from '@/api/payments';
import { CopyableText } from '@/components/copyable-text';
import {
  derivePaymentFileRowViewModel,
  getPaymentExternalRefDisplay,
  getPaymentSystemIdCopyValue,
  getPaymentSystemIdDisplay,
  PAYMENT_FILE_EMPTY_CELL,
  PAYMENT_FILE_UNKNOWN_LABEL,
} from '@/components/payment-file-row-view-model';
import { PAYMENT_FILE_DETAILS_TABLE_COL_SPAN } from '@/components/payment-file-table-columns';
import {
  ROW_ACTIVATOR_ROW_CLASS,
  RowActivator,
  shouldIgnoreRowClick,
} from '@/components/row-activator';
import { getStatusColor, getStatusLabel } from '@/components/status-chip';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const LOADING_CELL_LABEL = 'Loading…';
const FAILED_ROW_STATUS_LABEL = 'Failed';
const STATUS_ROW_LOADING_SPINNER_CLASS =
  'inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent';

function externalRefCellContent(value: string): string {
  return value.trim() || PAYMENT_FILE_EMPTY_CELL;
}

export interface PaymentFileDetailsTableProps {
  sortedRowResults: PaymentFileRowResult[] | null;
  payments: PaymentWithRelations[];
  paymentsError: string | null;
  isLoadingPayments: boolean;
  paymentCount: number;
  totalPaymentAmount: string | null;
  formatCurrency: (amount: number | string | null) => string;
  onPaymentClick?: (payment: PaymentWithRelations) => void;
  /** Column label from party setting external_ref_display_name (e.g. Case ID, External Ref) */
  externalRefLabel?: string;
  /** Hide the built-in badge breakdown (the detail subpage renders its own progress-bar version). */
  hideBreakdown?: boolean;
  hideHeader?: boolean;
  title?: string;
  recordLabel?: string;
}

type BreakdownEntry = { label: string; count: number; variant: 'secondary' | 'destructive' };

function buildPaymentBreakdown(
  payments: PaymentWithRelations[],
  rowResults: PaymentFileRowResult[] | null
): BreakdownEntry[] {
  const isFailedStub = (p: PaymentWithRelations) => p.status_id === PAYMENT_STATUS.FAILED;

  const methodCounts = payments
    .filter((p) => !isFailedStub(p))
    .reduce<Record<string, number>>((acc, p) => {
      const method = p.payment_method?.name || PAYMENT_FILE_UNKNOWN_LABEL;
      acc[method] = (acc[method] || 0) + 1;
      return acc;
    }, {});

  const failedRowCount =
    rowResults?.filter((r) => r.status === ROW_PROCESSING_STATUS.FAILED).length ?? 0;

  const entries: BreakdownEntry[] = Object.entries(methodCounts).map(([label, count]) => ({
    label,
    count,
    variant: 'secondary' as const,
  }));

  if (failedRowCount > 0) {
    entries.push({ label: 'Failed rows', count: failedRowCount, variant: 'destructive' });
  }

  return entries;
}

function PaymentMethodBreakdown({
  payments,
  rowResults,
}: {
  payments: PaymentWithRelations[];
  rowResults: PaymentFileRowResult[] | null;
}) {
  const entries = buildPaymentBreakdown(payments, rowResults);
  if (entries.length === 0) return null;

  return (
    <div className="px-4 py-3 border-t bg-muted/30">
      <span className="text-xs font-medium text-muted-foreground">Payment Method Breakdown</span>
      <div className="flex items-center gap-3 mt-1.5 flex-wrap">
        {entries.map(({ label, count, variant }) => (
          <Badge key={label} variant={variant} className="text-xs">
            {label}: {count}
          </Badge>
        ))}
      </div>
    </div>
  );
}

/**
 * Payments list for a payment file: same eight-column grid always (row #, ID, external ref,
 * payee, method, amount, status, reason). Body rows follow `row_results` when present, else
 * one row per payment with row/reason cells blank.
 */
export function PaymentFileDetailsTable({
  sortedRowResults,
  payments,
  paymentsError,
  isLoadingPayments,
  paymentCount,
  totalPaymentAmount,
  formatCurrency,
  onPaymentClick,
  externalRefLabel = 'External Ref',
  hideBreakdown = false,
  hideHeader = false,
  title: titleOverride,
  recordLabel: recordLabelOverride,
}: PaymentFileDetailsTableProps) {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopy = (copyValue: string, copyKey: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(copyValue);
    setCopiedId(copyKey);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const onCopyPaymentId = handleCopy;
  const title = titleOverride ?? (sortedRowResults ? 'Payment file rows' : 'Payments in this File');
  const recordLabel =
    recordLabelOverride ?? String(sortedRowResults ? sortedRowResults.length : paymentCount);

  return (
    <div className={hideHeader ? 'px-4 py-4' : 'px-6 pt-5 pb-4'}>
      {!hideHeader && (
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          <span className="text-xs text-muted-foreground">{recordLabel} records</span>
        </div>
      )}

      <div className="rounded-lg border overflow-hidden">
        {isLoadingPayments && !sortedRowResults ? (
          <div className="p-8 text-center text-muted-foreground">
            <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2" />
            <p>Loading payments...</p>
          </div>
        ) : paymentsError && !sortedRowResults ? (
          <div className="p-8 text-center text-muted-foreground">
            <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-destructive">{paymentsError}</p>
          </div>
        ) : (
          <>
            {paymentsError && sortedRowResults && (
              <div className="px-4 py-2 text-xs text-amber-800 bg-amber-50 border-b border-amber-200 dark:text-amber-200 dark:bg-amber-950/40 dark:border-amber-900/60">
                {paymentsError} Some payment details may be missing for successful rows.
              </div>
            )}
            {/* `title` already names this table on screen, except when the
                parent renders its own header -- the caption keeps the name
                either way. */}
            <Table className="w-max min-w-full" caption={title}>
              <TableHeader>
                <TableRow>
                  <TableHead className="px-4 w-[4.5rem]">Row</TableHead>
                  <TableHead className="px-4">ID</TableHead>
                  <TableHead className="px-4 whitespace-nowrap">{externalRefLabel}</TableHead>
                  <TableHead className="px-4">Payee</TableHead>
                  <TableHead className="px-4">Payment Method</TableHead>
                  <TableHead className="px-4">Amount</TableHead>
                  <TableHead className="px-4">Status</TableHead>
                  <TableHead className="px-4 whitespace-nowrap">Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedRowResults ? (
                  <RowResultsBody
                    sortedRowResults={sortedRowResults}
                    payments={payments}
                    isLoadingPayments={isLoadingPayments}
                    formatCurrency={formatCurrency}
                    copiedId={copiedId}
                    onCopyPaymentId={onCopyPaymentId}
                    {...(onPaymentClick ? { onPaymentClick } : {})}
                  />
                ) : payments.length > 0 ? (
                  payments.map((payment) => (
                    <PaymentOnlyRow
                      key={payment.id}
                      payment={payment}
                      copiedId={copiedId}
                      onCopyPaymentId={onCopyPaymentId}
                      formatCurrency={formatCurrency}
                      {...(onPaymentClick ? { onPaymentClick } : {})}
                    />
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={PAYMENT_FILE_DETAILS_TABLE_COL_SPAN}
                      className="text-center py-5 text-muted-foreground"
                    >
                      No payments found in this file.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {payments.length > 0 && (
              <div className="px-4 py-3 border-t bg-muted/30 flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Total</span>
                <span className="text-sm font-medium font-mono tabular-nums text-foreground">
                  {formatCurrency(totalPaymentAmount)}
                </span>
              </div>
            )}

            {!hideBreakdown && (
              <PaymentMethodBreakdown payments={payments} rowResults={sortedRowResults} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

type RowResultsBodyProps = Omit<
  PaymentFileDetailsTableProps,
  'sortedRowResults' | 'paymentsError' | 'paymentCount' | 'totalPaymentAmount'
> & {
  sortedRowResults: PaymentFileRowResult[];
  copiedId: string | null;
  onCopyPaymentId: (copyValue: string, copyKey: string, e: React.MouseEvent) => void;
};

/** When status relation is missing, avoid implying Pending Approval (see payment-file row_results + parallel fetch). */
function PaymentStatusBadgeFromPayment({ payment }: { payment: PaymentWithRelations }) {
  const rawId = payment.status?.id;
  if (rawId == null || rawId === 0) {
    return (
      <Badge variant="outline" className="font-normal text-muted-foreground">
        {PAYMENT_FILE_UNKNOWN_LABEL}
      </Badge>
    );
  }
  const statusId = rawId as PaymentStatusId;
  return (
    <Badge variant={getStatusColor(statusId, 'payment')}>
      {getStatusLabel(statusId, 'payment')}
    </Badge>
  );
}

function RowResultsBody({
  sortedRowResults,
  payments,
  isLoadingPayments,
  formatCurrency,
  copiedId,
  onCopyPaymentId,
  onPaymentClick,
}: RowResultsBodyProps) {
  if (sortedRowResults.length === 0) {
    return (
      <TableRow>
        <TableCell
          colSpan={PAYMENT_FILE_DETAILS_TABLE_COL_SPAN}
          className="text-center py-8 text-muted-foreground"
        >
          No row data for this file.
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      {sortedRowResults.map((result) => {
        const vm = derivePaymentFileRowViewModel(result, payments, isLoadingPayments);
        const activatable = Boolean(vm.isRowInteractive && vm.payment && onPaymentClick);

        return (
          <TableRow
            key={vm.rowKey}
            className={`group ${
              vm.isFailed
                ? 'bg-destructive/5 hover:bg-destructive/10'
                : activatable
                  ? `cursor-pointer hover:bg-muted/50 ${ROW_ACTIVATOR_ROW_CLASS}`
                  : ''
            }`}
            onClick={(event) => {
              if (!vm.isRowInteractive || !vm.payment) return;
              if (shouldIgnoreRowClick(event)) return;
              onPaymentClick?.(vm.payment);
            }}
          >
            <TableCell className="px-4 text-muted-foreground tabular-nums text-sm">
              {vm.rowNumber}
            </TableCell>
            <TableCell className="px-4">
              {vm.idCell.variant === 'withPayment' && vm.payment ? (
                <CopyableText
                  label={vm.displayRowId || PAYMENT_FILE_UNKNOWN_LABEL}
                  copyValue={vm.displayRowCopyValue}
                  isCopied={copiedId === vm.payment.id}
                  onCopy={(copyValue, event) =>
                    onCopyPaymentId(copyValue, vm.payment?.id ?? copyValue, event)
                  }
                />
              ) : vm.idCell.variant === 'csvCopy' ? (
                <CopyableText
                  label={vm.idCell.csvRef}
                  copyValue={vm.idCell.csvRef}
                  isCopied={copiedId === vm.idCell.csvRef}
                  onCopy={(copyValue, event) => onCopyPaymentId(copyValue, copyValue, event)}
                />
              ) : vm.idCell.variant === 'csvOnly' ? (
                <span
                  className="font-mono text-xs text-muted-foreground tabular-nums"
                  title={vm.idCell.csvRef}
                >
                  {vm.idCell.csvRef}
                </span>
              ) : vm.idCell.variant === 'loadingPayment' ? (
                <span className="text-xs text-muted-foreground">{LOADING_CELL_LABEL}</span>
              ) : (
                <span className="text-muted-foreground text-sm">{PAYMENT_FILE_EMPTY_CELL}</span>
              )}
            </TableCell>
            <TableCell className="px-4">
              <span className="font-mono text-xs text-muted-foreground tabular-nums">
                {externalRefCellContent(vm.displayExternalRef)}
              </span>
            </TableCell>
            <TableCell className="px-4">
              {activatable && vm.payment ? (
                // The id cell already holds a copy button, so the payee cell
                // carries the keyboard activator for the row.
                <RowActivator
                  label={`View payment ${vm.displayRowId || PAYMENT_FILE_UNKNOWN_LABEL}`}
                  onActivate={() => {
                    if (vm.payment) onPaymentClick?.(vm.payment);
                  }}
                >
                  {vm.payeeLabel}
                </RowActivator>
              ) : (
                vm.payeeLabel
              )}
            </TableCell>
            <TableCell className="px-4">
              {vm.payment?.payment_method?.name ?? PAYMENT_FILE_EMPTY_CELL}
            </TableCell>
            <TableCell className="px-4 font-mono text-sm tabular-nums">
              {vm.amountLabel != null && vm.amountLabel !== ''
                ? formatCurrency(vm.amountLabel)
                : PAYMENT_FILE_EMPTY_CELL}
            </TableCell>
            <TableCell className="px-4">
              {vm.statusCell.variant === 'failed' ? (
                <Badge variant="destructive">{FAILED_ROW_STATUS_LABEL}</Badge>
              ) : vm.statusCell.variant === 'loadingPayment' ? (
                <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span className={STATUS_ROW_LOADING_SPINNER_CLASS} aria-hidden />
                  {LOADING_CELL_LABEL}
                </span>
              ) : vm.statusCell.variant === 'withPayment' ? (
                <PaymentStatusBadgeFromPayment payment={vm.statusCell.payment} />
              ) : (
                <Badge variant="outline" className="font-normal text-muted-foreground">
                  {PAYMENT_FILE_EMPTY_CELL}
                </Badge>
              )}
            </TableCell>
            <TableCell
              className={`px-4 text-sm whitespace-nowrap ${
                vm.isFailed ? 'font-medium text-destructive' : 'text-muted-foreground'
              }`}
            >
              {vm.isFailed
                ? getPaymentFileRowFailureDisplayMessage(vm.result)
                : PAYMENT_FILE_EMPTY_CELL}
            </TableCell>
          </TableRow>
        );
      })}
    </>
  );
}

function PaymentOnlyRow({
  payment,
  copiedId,
  onCopyPaymentId,
  onPaymentClick,
  formatCurrency,
}: {
  payment: PaymentWithRelations;
  copiedId: string | null;
  onCopyPaymentId: (copyValue: string, copyKey: string, e: React.MouseEvent) => void;
  onPaymentClick?: (payment: PaymentWithRelations) => void;
  formatCurrency: (amount: number | string | null) => string;
}) {
  return (
    <TableRow
      className={`group ${
        onPaymentClick ? `cursor-pointer hover:bg-muted/50 ${ROW_ACTIVATOR_ROW_CLASS}` : ''
      }`}
      onClick={(event) => {
        if (shouldIgnoreRowClick(event)) return;
        onPaymentClick?.(payment);
      }}
    >
      <TableCell className="px-4 text-muted-foreground tabular-nums text-sm">
        {PAYMENT_FILE_EMPTY_CELL}
      </TableCell>
      <TableCell className="px-4">
        <CopyableText
          label={getPaymentSystemIdDisplay(payment) || PAYMENT_FILE_UNKNOWN_LABEL}
          copyValue={getPaymentSystemIdCopyValue(payment)}
          isCopied={copiedId === payment.id}
          onCopy={(copyValue, event) => onCopyPaymentId(copyValue, payment.id, event)}
        />
      </TableCell>
      <TableCell className="px-4">
        <span className="font-mono text-xs text-muted-foreground tabular-nums">
          {externalRefCellContent(getPaymentExternalRefDisplay(payment))}
        </span>
      </TableCell>
      <TableCell className="px-4">
        {onPaymentClick ? (
          <RowActivator
            label={`View payment ${getPaymentSystemIdDisplay(payment) || PAYMENT_FILE_UNKNOWN_LABEL}`}
            onActivate={() => onPaymentClick(payment)}
          >
            {payment.payee_name || payment.payee_party?.party_name || PAYMENT_FILE_EMPTY_CELL}
          </RowActivator>
        ) : (
          payment.payee_name || payment.payee_party?.party_name || PAYMENT_FILE_EMPTY_CELL
        )}
      </TableCell>
      <TableCell className="px-4">
        {payment.payment_method?.name || PAYMENT_FILE_EMPTY_CELL}
      </TableCell>
      <TableCell className="px-4 font-mono text-sm tabular-nums">
        {formatCurrency(payment.amount_total)}
      </TableCell>
      <TableCell className="px-4">
        <PaymentStatusBadgeFromPayment payment={payment} />
      </TableCell>
      <TableCell className="px-4 text-sm text-muted-foreground whitespace-nowrap">
        {PAYMENT_FILE_EMPTY_CELL}
      </TableCell>
    </TableRow>
  );
}
